import { ApiError, post, setToken } from '../api.js';
import { button, el, field, input, panel, submitButton, toast } from '../ui.js';

const MARK = `<svg viewBox="0 0 48 48" aria-hidden="true">
  <defs><linearGradient id="cvm" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0" stop-color="var(--copper)"/><stop offset="1" stop-color="var(--crit)"/>
  </linearGradient></defs>
  <path d="M36.63 12.63 A17 17 0 1 0 36.63 35.37" fill="none" stroke="url(#cvm)" stroke-width="5.4" stroke-linecap="round"/>
  <path d="M29.45 16.22 A9.5 9.5 0 1 0 29.45 31.78" fill="none" stroke="url(#cvm)" stroke-width="3.9" stroke-linecap="round"/>
  <circle cx="24" cy="24" r="3.5" fill="url(#cvm)"/>
  <circle cx="41.6" cy="24" r="2.3" fill="var(--copper)"/>
  <circle cx="46.4" cy="24" r="1.3" fill="var(--copper)" opacity=".5"/>
</svg>`;

function shell(title: string, blurb: string, form: HTMLElement, footer?: HTMLElement): HTMLElement {
  const mark = el('div', { style: 'width:46px;height:46px;margin:0 auto 14px', html: MARK });
  return el('div', { class: 'center' },
    mark,
    el('h1', {
      style: 'font-family:var(--f-display);font-weight:700;font-size:24px;text-align:center;letter-spacing:.02em',
      text: title,
    }),
    el('p', { style: 'text-align:center;color:var(--ink-3);font-size:13px;margin:8px 0 20px', text: blurb }),
    panel(null, form),
    footer ?? null);
}

/** One error surface, so a failure never disappears into the console. */
function errorBox(): { node: HTMLElement; show: (e: unknown) => void; clear: () => void } {
  const node = el('div', { class: 'warnbox', style: 'display:none;margin-bottom:12px', role: 'alert' });
  return {
    node,
    show(e: unknown) {
      const message = e instanceof ApiError ? e.message : 'Something went wrong. Try again.';
      node.textContent = message;
      node.style.display = '';
    },
    clear() { node.style.display = 'none'; },
  };
}

interface AuthOk {
  token: string;
  mfaRequired?: boolean;
  /** Which second factors this account can use right now. */
  mfaMethods?: Array<'totp' | 'phone' | 'recovery'>;
  /** Masked destination of a texted code, when a phone is enrolled. */
  phoneHint?: string | null;
  /** False when the server has no text-message provider configured. */
  smsAvailable?: boolean;
}

interface CodeSent { hint: string; expiresAt: string; devCode?: string }

export function signInView(onDone: () => void, go: (p: string) => void): HTMLElement {
  const err = errorBox();
  const email = input({ type: 'email', autocomplete: 'username', required: true, placeholder: 'you@agency.com' });
  const password = input({ type: 'password', autocomplete: 'current-password', required: true });
  const agencyWrap = el('div');
  let agency: string | undefined;

  /* ------------------------------------------------------ second factor --
   * The password stage and the code stage are separate on purpose. A code
   * that arrives by text cannot be typed before the form is submitted, so
   * asking for it up front — as a single-stage form must — is asking for
   * something the person does not have yet.
   */
  const mfa = input({
    inputmode: 'numeric', autocomplete: 'one-time-code',
    placeholder: '000000', maxlength: '9', 'aria-label': 'Authentication code',
  });
  const mfaHelp = el('p', { class: 'mfa-help' });
  const sendBtn = button('Text me a code', 'ghost block');
  const verifyBtn = submitButton('Verify', 'pri block');
  const mfaErr = errorBox();
  const mfaStage = el('form', {
    class: 'mfa-stage',
    onsubmit: async (e: Event) => {
      e.preventDefault();
      mfaErr.clear();
      const code = mfa.value.trim();
      if (!code) { mfa.focus(); return; }
      verifyBtn.disabled = true;
      verifyBtn.textContent = 'Checking…';
      try {
        const res = await post<AuthOk>('/v1/auth/mfa/verify', { code });
        setToken(res.token);
        onDone();
      } catch (e2) {
        mfaErr.show(e2);
        mfa.select();
      } finally {
        verifyBtn.disabled = false;
        verifyBtn.textContent = 'Verify';
      }
    },
  },
    mfaErr.node,
    mfaHelp,
    field('Authentication code', mfa),
    verifyBtn,
    sendBtn);
  mfaStage.style.display = 'none';

  /** Counts down the resend cooldown so the button is never a dead end. */
  let cooldown = 0;
  let ticker: ReturnType<typeof setInterval> | null = null;
  const paintSend = (): void => {
    sendBtn.disabled = cooldown > 0;
    sendBtn.textContent = cooldown > 0 ? `Send another in ${cooldown}s` : 'Text me a code';
  };
  const startCooldown = (seconds: number): void => {
    cooldown = seconds;
    paintSend();
    if (ticker) clearInterval(ticker);
    ticker = setInterval(() => {
      cooldown -= 1;
      if (cooldown <= 0 && ticker) { clearInterval(ticker); ticker = null; }
      paintSend();
    }, 1000);
  };

  sendBtn.addEventListener('click', async () => {
    mfaErr.clear();
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending…';
    try {
      const sent = await post<CodeSent>('/v1/auth/mfa/send', {});
      mfaHelp.textContent = `We sent a six-digit code to ${sent.hint}. It expires in five minutes.`;
      mfa.focus();
      startCooldown(30);
      // Only ever present when the server has no provider configured, which
      // cannot be the case in production.
      if (sent.devCode) toast(`Development build — the code is ${sent.devCode}`, 'info');
    } catch (e2) {
      mfaErr.show(e2);
      paintSend();
    }
  });

  /** Moves the form from the password stage to the code stage. */
  const enterMfaStage = (res: AuthOk): void => {
    const methods = res.mfaMethods ?? ['totp'];
    form.style.display = 'none';
    mfaStage.style.display = '';
    const canPhone = methods.includes('phone');
    const canTotp = methods.includes('totp');
    sendBtn.style.display = canPhone ? '' : 'none';
    // Name only the factors this account actually has. Telling somebody to
    // open an authenticator app they never set up is how a sign-in becomes a
    // support call.
    mfaHelp.textContent = canPhone && canTotp
      ? `Enter the code from your authenticator app, or have one texted to ${res.phoneHint ?? 'your phone'}.`
      : canPhone
        ? `Have a six-digit code texted to ${res.phoneHint ?? 'your phone'}, then enter it below. A recovery code works too.`
        : canTotp
          ? 'Enter the six-digit code from your authenticator app, or one of your recovery codes.'
          : 'Enter one of your recovery codes.';
    mfa.focus();
  };

  const submit = submitButton('Sign in', 'pri block');
  const form = el('form', {
    onsubmit: async (e: Event) => {
      e.preventDefault();
      err.clear();
      submit.disabled = true;
      submit.textContent = 'Signing in…';
      try {
        const res = await post<AuthOk>('/v1/auth/sign-in', {
          email: email.value,
          password: password.value,
          agency,
          mfaCode: mfa.value || undefined,
        });
        setToken(res.token);
        if (res.mfaRequired) enterMfaStage(res);
        else onDone();
      } catch (e2) {
        if (e2 instanceof ApiError && e2.code === 'agency_required') {
          const list = (e2.details as { agencies: Array<{ slug: string; name: string }> }).agencies;
          agencyWrap.replaceChildren(field(
            'Which agency?',
            (() => {
              const s = el('select', { class: 'inp' });
              s.append(el('option', { value: '' }, 'Choose…'));
              for (const a of list) s.append(el('option', { value: a.slug }, a.name));
              s.addEventListener('change', () => { agency = s.value || undefined; });
              return s;
            })(),
            'That email address is used at more than one agency.',
          ));
        } else err.show(e2);
      } finally {
        submit.disabled = false;
        submit.textContent = 'Sign in';
      }
    },
  },
    err.node,
    field('Email', email),
    field('Password', password),
    agencyWrap,
    submit);

  return shell('Sign in to Cellvora', 'One sign-in for everyone at your agency.',
    el('div', null, form, mfaStage),
    el('div', null,
      el('div', { class: 'auth-roles' },
        el('p', null, 'Agency owners, administrators, managers, team leaders, agents and compliance staff all sign in here. What you can see is decided by your account — there is no separate admin login to find.'),
        el('ul', null,
          el('li', null, el('b', null, 'Agents '), 'go straight to their own queue.'),
          el('li', null, el('b', null, 'Team leaders '), 'see their whole team.'),
          el('li', null, el('b', null, 'Agency owners and administrators '), 'see their whole agency.'),
          el('li', null, el('b', null, 'Compliance '), 'can read everything and change nothing.'))),
      el('p', { style: 'text-align:center;font-size:12.5px;color:var(--ink-3);margin-top:16px' },
        'Invited by somebody? Use the link they sent you. ',
        el('br', null),
        'Starting an agency? ',
        el('a', { href: '#/sign-up', onclick: (e: Event) => { e.preventDefault(); go('/sign-up'); } }, 'Create one')),
      el('p', { style: 'text-align:center;font-size:12.5px;margin-top:10px' },
        el('a', { href: '#/', onclick: (e: Event) => { e.preventDefault(); go('/'); } }, '← What is Cellvora?'))));
}

export function signUpView(onDone: () => void, go: (p: string) => void): HTMLElement {
  const err = errorBox();
  const agencyName = input({ required: true, placeholder: 'Your agency\u2019s legal name', autocomplete: 'organization' });
  const firstName = input({ required: true, autocomplete: 'given-name' });
  const lastName = input({ required: true, autocomplete: 'family-name' });
  const email = input({ type: 'email', required: true, autocomplete: 'username' });
  const password = input({ type: 'password', required: true, autocomplete: 'new-password', minlength: '12' });
  const submit = submitButton('Create the agency', 'pri block');

  const form = el('form', {
    onsubmit: async (e: Event) => {
      e.preventDefault();
      err.clear();
      submit.disabled = true;
      submit.textContent = 'Creating…';
      try {
        const res = await post<AuthOk>('/v1/auth/sign-up', {
          agencyName: agencyName.value,
          firstName: firstName.value,
          lastName: lastName.value,
          email: email.value,
          password: password.value,
        });
        setToken(res.token);
        onDone();
      } catch (e2) { err.show(e2); } finally {
        submit.disabled = false;
        submit.textContent = 'Create the agency';
      }
    },
  },
    err.node,
    field('Agency name', agencyName, 'This is what your agents will see. It has to be unique.'),
    el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:10px' },
      field('First name', firstName),
      field('Last name', lastName)),
    field('Your email', email),
    field('Password', password, 'At least 12 characters. Use a passphrase you can remember.'),
    submit);

  return shell('Create your agency', 'You will be its agency owner. Everyone else joins by invitation from you.', form,
    el('div', null,
      el('p', { style: 'text-align:center;font-size:12.5px;color:var(--ink-3);margin-top:14px' },
        'Already have an account? ',
        el('a', { href: '#/sign-in', onclick: (e: Event) => { e.preventDefault(); go('/sign-in'); } }, 'Sign in')),
      el('p', { style: 'text-align:center;font-size:12.5px;margin-top:10px' },
        el('a', { href: '#/', onclick: (e: Event) => { e.preventDefault(); go('/'); } }, '← What is Cellvora?'))));
}

export function inviteView(token: string, onDone: () => void): HTMLElement {
  const err = errorBox();
  const firstName = input({ required: true, autocomplete: 'given-name' });
  const lastName = input({ required: true, autocomplete: 'family-name' });
  const email = input({ type: 'email', required: true, autocomplete: 'email' });
  const password = input({ type: 'password', required: true, autocomplete: 'new-password', minlength: '12' });
  const submit = submitButton('Join the agency', 'pri block');

  /**
   * Two kinds of link arrive here and they ask for different things.
   *
   * An invitation sent to one address already knows who this is, so asking
   * again is a question with only one right answer and a way to get it wrong.
   * A join link posted in an agency's group chat named nobody, so the person
   * joining has to say who they are.
   *
   * The field is shown for both until the server says which this is, then
   * hidden if it turns out to be unnecessary — better than flashing a field
   * that vanishes, and far better than not asking when it was needed.
   */
  const emailField = field('Your work email', email,
    'The address your agency knows you by.');
  emailField.hidden = true;

  void (async () => {
    try {
      const info = await post<{ openLink: boolean }>('/v1/auth/invite-info', { token });
      emailField.hidden = !info.openLink;
      email.required = info.openLink;
    } catch {
      // If the check fails the link is probably bad, and the submit below will
      // say so properly. Showing the field is the safe guess in the meantime.
      emailField.hidden = false;
    }
  })();

  const form = el('form', {
    onsubmit: async (e: Event) => {
      e.preventDefault();
      err.clear();
      submit.disabled = true;
      try {
        const res = await post<AuthOk>('/v1/auth/accept-invite', {
          token,
          firstName: firstName.value,
          lastName: lastName.value,
          password: password.value,
          ...(emailField.hidden ? {} : { email: email.value }),
        });
        setToken(res.token);
        onDone();
      } catch (e2) { err.show(e2); } finally { submit.disabled = false; }
    },
  },
    err.node,
    el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:10px' },
      field('First name', firstName),
      field('Last name', lastName)),
    emailField,
    field('Choose a password', password, 'At least 12 characters. Nobody at your agency can see it.'),
    submit);

  return shell('You have been invited', 'Set a password and you are in.', form);
}
